import React, { useCallback, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";
import { useTranslation } from "react-i18next";

import * as yup from "yup";

import map from "lodash/map";

import clsx from "clsx";
import AsyncSelect from "react-select/async";

import { toast } from "react-toastify";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  useRouteMatch,
  useHistory,
  Route,
  Link,
  Switch,
} from "react-router-dom";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { EntityType } from "../../../config/const/entity";

import {
  QUERY_ENTITIES_PARENTS,
  QUERY_VISIONECT_ITEM,
  QUERY_VISIONECT_ITEMS,
} from "../../../config/graphql/query";
import {
  UPDATE_VISIONECT_ITEM,
  CREATE_VISIONECT_ITEM,
  DELETE_VISIONECT_ITEM,
} from "../../../config/graphql/mutation";
import { Directions } from "../../../config/const/common";
import Table from "../../../components/Table";
import RoleBlock from "../../../components/RoleBlock";

const WayfindingItemsList = () => {
  const history = useHistory();
  const {
    url,
    // @ts-ignore
    params: { wayfindingId: id },
  } = useRouteMatch();

  const { t } = useTranslation(["wayfinding, common"]);

  const { data } = useQuery(QUERY_VISIONECT_ITEMS, {
    variables: {
      filter: {
        visionect: id,
      },
    },
  });

  const items = data?.visionectItems ?? [];

  const renderItem = useCallback(
    ({ id, entity }: IVisionectItem) => {
      const propertiesTitle = map(entity.properties, "title").join(", ");

      return (
        <tr
          key={`visionect-${id}`}
          onClick={() => history.push(`${url}/${id}`)}
        >
          <th scope="row">{id}</th>
          <td>{entity?.title}</td>
          <td>{propertiesTitle}</td>
        </tr>
      );
    },
    [history, url],
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center my-4">
        <div>
          <Link to={`${url}/new`} type="button" className="btn btn-primary">
            {t("wayfinding:wayfinding.wayfindingItemList.button.add")}
          </Link>
        </div>
      </div>
      <Table>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">
              {t("wayfinding:wayfinding.wayfindingItemList.th.entity")}
            </th>
            <th scope="col">
              {t("wayfinding:wayfinding.wayfindingItemList.th.properties")}
            </th>
          </tr>
        </thead>
        <tbody>{items.map(renderItem)}</tbody>
      </Table>
    </>
  );
};

const WayfindingItem = () => {
  const [show, setShow] = useToggle(false);

  const { t } = useTranslation(["wayfinding, common"]);

  const history = useHistory();
  const {
    // @ts-ignore
    params: { wayfindingId: wayfinding, id },
  } = useRouteMatch();

  const graphql = useApolloClient();

  const schema = useMemo(
    () =>
      yup.object().shape({
        direction: yup.string().required(),
        entity: yup
          .object()
          .shape({
            id: yup
              .string()
              .required(t("wayfinding:wayfinding.wayfindingItem.yup.entity")),
            title: yup.string(),
          })
          .required(),
      }),
    [t],
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const { data: itemData, loading } = useQuery(QUERY_VISIONECT_ITEM, {
    skip: !id,
    variables: { id },
  });

  useEffect(() => {
    methods.reset(itemData?.visionectItem ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData]);

  const [onSave, { loading: saving }] = useMutation(
    id ? UPDATE_VISIONECT_ITEM : CREATE_VISIONECT_ITEM,
    {
      refetchQueries: [
        {
          query: QUERY_VISIONECT_ITEMS,
          variables: {
            filter: {
              visionect: wayfinding,
            },
          },
        },
      ],
    },
  );

  const [onDelete] = useMutation(DELETE_VISIONECT_ITEM, {
    refetchQueries: [
      {
        query: QUERY_VISIONECT_ITEMS,
        variables: {
          filter: {
            visionect: wayfinding,
          },
        },
      },
    ],
  });

  const onRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDelete({ variables: { id } })
        .then(() => {
          history.replace(history.location.pathname.replace(`/${id}`, ""));

          toast.success<string>(
            t("wayfinding:wayfinding.wayfindingItem.toast.deleted"),
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [onDelete, id, history, t],
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setShow(true);
    },
    [setShow],
  );

  const onSubmit = useCallback(
    ({ entity, ...rest }: any) => {
      const input = {
        ...rest,
        entity: entity.id,
        ...(!!id && { id }),
        ...(!id && { visionect: wayfinding }),
      };

      return onSave({ variables: { input } })
        .then(({ data }) => {
          if (data?.createVisionectItem?.id) {
            history.replace(
              `/wayfinding/${wayfinding}/item/${data?.createVisionectItem?.id}`,
            );

            toast.success<string>(
              t("wayfinding:wayfinding.wayfindingItem.toast.created"),
            );
            return;
          }

          toast.success<string>(
            t("wayfinding:wayfinding.wayfindingItem.toast.updated"),
          );
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [id, wayfinding, onSave, history, t],
  );

  const getOptionLabel = useCallback(
    ({ title, properties, parents }: TEntity) => {
      const propertiesString =
        map(properties, "title").filter(Boolean).join(", ") || "N/A";

      const entityTitle = [...map(parents, "title").reverse(), title]
        .filter(Boolean)
        .join(" - ");

      return [propertiesString, `(${entityTitle})`].filter(Boolean).join(" - ");
    },
    [],
  );

  const onLoadOptions = useCallback(
    (value: string) => {
      return graphql
        .query({
          query: QUERY_ENTITIES_PARENTS,
          variables: {
            filter: {
              title: value,
              type: EntityType.Space,
            },
          },
          fetchPolicy: "network-only",
        })
        .then(({ data }) => data?.entities ?? [])
        .catch(() => {
          return [];
        });
    },
    [graphql],
  );

  return (
    <>
      <FormProvider {...methods}>
        <div className="tab-pane active" role="tabpanel">
          <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="col-lg-4">
              <Controller
                control={methods.control}
                name="entity"
                render={({ field, fieldState: { error } }) => (
                  <div className="form-group">
                    <label htmlFor="entity">
                      {t("wayfinding:wayfinding.wayfindingItem.form.entity")}
                    </label>
                    <AsyncSelect
                      defaultOptions
                      className={clsx({
                        "is-invalid": !!error,
                      })}
                      getOptionLabel={getOptionLabel}
                      getOptionValue={({ id }) => id}
                      loadOptions={onLoadOptions}
                      {...field}
                    />
                    {!!error && (
                      <div className="invalid-feedback">{error.message}</div>
                    )}
                    <small id="companyHelp" className="form-text text-muted">
                      {t(
                        "wayfinding:wayfinding.wayfindingItem.form.companyHelp",
                      )}
                    </small>
                  </div>
                )}
              />

              <div className="form-group">
                <label htmlFor="type">
                  {t("wayfinding:wayfinding.wayfindingItem.form.direction")}
                </label>
                <select
                  {...methods.register("direction")}
                  className="custom-select"
                >
                  {Directions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12">
              <input
                type="submit"
                className="btn btn-primary"
                disabled={saving || loading}
              />
              <RoleBlock roles={["ADMIN"]}>
                {id && (
                  <button
                    onClick={onBeforeRemove}
                    className="btn btn-danger ml-3"
                  >
                    {t("common:delete")}
                  </button>
                )}
              </RoleBlock>
            </div>
          </form>
        </div>
      </FormProvider>
      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("wayfinding:wayfinding.wayfindingItem.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("wayfinding:wayfinding.wayfindingItem.modal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t("common:cancel")}
          </Button>
          <Button variant="danger" onClick={onRemove}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const WayfindingItems = React.memo(() => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={`${path}`}>
        <WayfindingItemsList />
      </Route>
      <Route exact path={`${path}/new`}>
        <WayfindingItem />
      </Route>
      <Route exact path={`${path}/:id`}>
        <WayfindingItem />
      </Route>
    </Switch>
  );
});

export default WayfindingItems;
